<script setup>
import { reactive, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, minValue } from '@vuelidate/validators';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();
const store = useStore();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getWahaInboxes'),
};

onMounted(() => {
  if (!formState.labels.value?.length) {
    store.dispatch('labels/get');
  }
});

const getInitialState = () => ({
  title: '',
  message: '',
  inboxId: null,
  scheduledAt: null,
  selectedAudienceLabels: [],
  messageDelay: 1,
  delayUnit: 'minutes',
  delayVariationPercent: 0,
  variationEnabled: false,
  preventDuplicateContacts: true,
  autoEnqueueByLabel: false,
});

const state = reactive(getInitialState());

const rules = {
  title: { required, minLength: minLength(1) },
  message: { required, minLength: minLength(1) },
  inboxId: { required },
  scheduledAt: { required },
  selectedAudienceLabels: { required },
  messageDelay: { required, minValue: minValue(1) },
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({ value: item[valueKey], label: item[labelKey] })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const delayUnitOptions = computed(() => [
  {
    value: 'minutes',
    label: t('CAMPAIGN.WAHA.CREATE.FORM.CADENCE.UNIT_MINUTES'),
  },
  { value: 'hours', label: t('CAMPAIGN.WAHA.CREATE.FORM.CADENCE.UNIT_HOURS') },
  { value: 'days', label: t('CAMPAIGN.WAHA.CREATE.FORM.CADENCE.UNIT_DAYS') },
]);

const variationPreview = computed(() => {
  if (!state.variationEnabled || !state.messageDelay) return null;
  const base = Number(state.messageDelay);
  const pct = Number(state.delayVariationPercent) / 100;
  const max = Math.ceil(base * (1 + pct));
  const unit = t(
    `CAMPAIGN.WAHA.CREATE.FORM.CADENCE.UNIT_${state.delayUnit.toUpperCase()}`
  );
  return `${base}–${max} ${unit}`;
});

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.WAHA.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  message: getErrorMessage('message', 'MESSAGE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
  audience: getErrorMessage('selectedAudienceLabels', 'AUDIENCE_LABELS'),
  messageDelay: getErrorMessage('messageDelay', 'CADENCE'),
}));

const isSubmitDisabled = computed(() => v$.value.$invalid);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const resetState = () => {
  Object.assign(state, getInitialState());
  v$.value.$reset();
};

const handleCancel = () => emit('cancel');

const prepareCampaignDetails = () => ({
  title: state.title,
  message: state.message,
  inbox_id: state.inboxId,
  scheduled_at: formatToUTCString(state.scheduledAt),
  audience: state.selectedAudienceLabels?.map(id => ({ id, type: 'Label' })),
  message_delay: Number(state.messageDelay),
  delay_unit: state.delayUnit,
  delay_variation_percent: state.variationEnabled
    ? Number(state.delayVariationPercent)
    : 0,
  prevent_duplicate_contacts: state.preventDuplicateContacts,
  auto_enqueue_by_label: state.autoEnqueueByLabel,
});

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  emit('submit', prepareCampaignDetails());
  resetState();
  handleCancel();
};
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.WAHA.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.WAHA.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <TextArea
      v-model="state.message"
      :label="t('CAMPAIGN.WAHA.CREATE.FORM.MESSAGE.LABEL')"
      :placeholder="t('CAMPAIGN.WAHA.CREATE.FORM.MESSAGE.PLACEHOLDER')"
      show-character-count
      :message="formErrors.message"
      :message-type="formErrors.message ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label
        for="waha-inbox"
        class="mb-0.5 text-sm font-medium text-n-slate-12"
      >
        {{ t('CAMPAIGN.WAHA.CREATE.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="waha-inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.WAHA.CREATE.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <div class="flex flex-col gap-1">
      <label
        for="waha-audience"
        class="mb-0.5 text-sm font-medium text-n-slate-12"
      >
        {{ t('CAMPAIGN.WAHA.CREATE.FORM.AUDIENCE_LABELS.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="state.selectedAudienceLabels"
        :options="audienceList"
        :label="t('CAMPAIGN.WAHA.CREATE.FORM.AUDIENCE_LABELS.LABEL')"
        :placeholder="
          t('CAMPAIGN.WAHA.CREATE.FORM.AUDIENCE_LABELS.PLACEHOLDER')
        "
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.WAHA.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.WAHA.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <!-- Cadência -->
    <div
      class="flex flex-col gap-3 p-4 rounded-lg bg-n-surface-2 border border-n-weak"
    >
      <p class="text-sm font-semibold text-n-slate-12">
        {{ t('CAMPAIGN.WAHA.CREATE.FORM.CADENCE.SECTION_TITLE') }}
      </p>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WAHA.CREATE.FORM.CADENCE.DELAY_LABEL') }}
        </label>
        <div class="flex gap-2">
          <Input
            v-model="state.messageDelay"
            type="number"
            min="1"
            class="w-24"
            :message="formErrors.messageDelay"
            :message-type="formErrors.messageDelay ? 'error' : 'info'"
          />
          <div class="flex-1">
            <ComboBox
              v-model="state.delayUnit"
              :options="delayUnitOptions"
              class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak"
            />
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <div class="flex items-center justify-between">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.WAHA.CREATE.FORM.CADENCE.VARIATION_LABEL') }}
          </label>
          <input
            v-model="state.variationEnabled"
            type="checkbox"
            class="w-4 h-4 accent-n-brand"
          />
        </div>
        <div v-if="state.variationEnabled" class="flex flex-col gap-1">
          <div class="flex items-center gap-2">
            <Input
              v-model="state.delayVariationPercent"
              type="number"
              min="1"
              max="200"
              class="w-24"
            />
            <span class="text-sm text-n-slate-11">{{
              t('CAMPAIGN.WAHA.CREATE.FORM.CADENCE.PERCENT_SYMBOL')
            }}</span>
          </div>
          <p v-if="variationPreview" class="text-xs text-n-slate-10">
            {{
              t('CAMPAIGN.WAHA.CREATE.FORM.CADENCE.VARIATION_HINT', {
                range: variationPreview,
              })
            }}
          </p>
        </div>
      </div>

      <div
        class="flex items-center justify-between pt-1 border-t border-n-weak"
      >
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WAHA.CREATE.FORM.CADENCE.DEDUP_LABEL') }}
        </label>
        <input
          v-model="state.preventDuplicateContacts"
          type="checkbox"
          class="w-4 h-4 accent-n-brand"
        />
      </div>

      <div class="flex items-center justify-between">
        <div class="flex flex-col gap-0.5">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.WAHA.CREATE.FORM.CADENCE.AUTO_ENQUEUE_LABEL') }}
          </label>
          <p class="text-xs text-n-slate-10">
            {{ t('CAMPAIGN.WAHA.CREATE.FORM.CADENCE.AUTO_ENQUEUE_HINT') }}
          </p>
        </div>
        <input
          v-model="state.autoEnqueueByLabel"
          type="checkbox"
          class="w-4 h-4 accent-n-brand"
        />
      </div>
    </div>

    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.WAHA.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.WAHA.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
